import {
  Chapter,
  ChapterLesson,
  Course,
  CourseChapter,
  CourseWithChapters,
  Lesson,
  LessonLink,
  LessonResult,
} from "@jelly/lesson";
import data from "../data/course";
import config from "appConfig";
import { LmsCourseProgress } from "../interfaces/LmsCourseProgress";
import { CourseLessonProgress } from "../interfaces/CourseLessonProgress";

export function getLinkForCourseLesson(courseId: string, lessonId: string): string {
  return `${config.LMS_LANDING_PAGE_PATH || ""}/course/${courseId}/${lessonId}`;
}

export function formatLessonsForChapter(lessons: Lesson[], startPosition = 0): ChapterLesson[] {
  return lessons.map((lesson) => {
    return {
      lesson: {
        id: lesson.id,
        publicId: lesson.publicId,
        name: lesson.name,
        difficulty: lesson.difficulty,
        contentTypes: lesson.contentTypes,
        pdfFormatUrl: lesson.pdfFormatUrl,
        body: [], // we don't need the body here
      },
      position: startPosition++,
    };
  });
}

export function findChapterForLinkByCourseLesson(course: Course, lesson: Lesson): LessonLink["chapter"] {
  const courseChapters = data.byId[course.id].chapters;
  const chapter = courseChapters.find((chapter) => {
    return chapter.chapterLessons.find((chapterLesson) => {
      return chapterLesson.lesson.id === lesson.id;
    });
  }) as CourseChapter;

  return {
    id: chapter.id,
    name: chapter.name,
  };
}

export function getChapters(course?: CourseWithChapters, lessonResults?: LessonResult[]): Chapter[] {
  if (!course) {
    return [];
  }
  if (!lessonResults) {
    return [];
  }

  const chapters: Chapter[] = course.chapters.map((chapter) => ({
    name: chapter.name,
    items: chapter.chapterLessons.map((chapterLesson) => {
      const lessonResult = lessonResults.find((item) => item.lesson === chapterLesson.lesson.id);

      return {
        id: chapterLesson.lesson.id,
        number: `${course.publicId}.${chapterLesson.lesson.publicId}`,
        label: chapterLesson.lesson.name,
        url: `/course/${course.id}/${chapterLesson.lesson.id}`,
        completed: lessonResult ? lessonResult.isComplete : false,
      };
    }),
  }));

  chapters.unshift({
    name: null,
    items: [
      {
        id: `welcome`,
        number: `${course.publicId}.0`,
        completed: true,
        label: "Welcome page",
        url: `/course/${course.id}`,
      },
    ],
  });

  return chapters;
}

export function getCourseLessonsProgress(
  course?: CourseWithChapters,
  lessonsResult?: LessonResult[],
  courseProgress?: LmsCourseProgress
): CourseLessonProgress[] {
  if (!course || !courseProgress) {
    return [];
  }

  const lessonsProgress: CourseLessonProgress[] = [];

  course.chapters.forEach((chapter) => {
    chapter.chapterLessons.forEach((chapterLesson) => {
      const lessonResult = lessonsResult?.find((item) => item.lesson === chapterLesson.lesson.id);
      const tasksProgress = courseProgress.lessons.find(
        (lessonProgress) => lessonProgress.id === chapterLesson.lesson.id
      );

      lessonsProgress.push({
        id: chapterLesson.lesson.id,
        number: `${course.publicId}.${chapterLesson.lesson.publicId}`,
        name: chapterLesson.lesson.name,
        completed: lessonResult ? lessonResult.isComplete : false,
        difficulty: chapterLesson.lesson.difficulty || 0,
        contentType: {
          video: chapterLesson.lesson.contentTypes?.video || false,
          audio: chapterLesson.lesson.contentTypes?.audio || false,
          interactive: chapterLesson.lesson.contentTypes?.interactive || false,
        },
        tasks:
          tasksProgress?.tasks.map((taskProgress) => {
            return {
              task: taskProgress.id,
              progress: {
                isCorrect: taskProgress.isCorrect,
                isComplete: taskProgress.isComplete,
              },
            };
          }) || [],
      });
    });
  });

  return lessonsProgress;
}
